import React, { useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { BasicDataFormVM } from './BasicDataFormVM';
import { SlopeCalc } from '../SlopeCalc/SlopeCalc';
import { Alert } from '../../common/Alert/Alert';
import { LoadingSpinner } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import './basicDataForm.scss';
import stores from '../../../models/Stores';
import { useHistory } from 'react-router-dom';
import { Modal, ModalHeader, ModalFooter, ModalBody } from 'reactstrap';
import { mdiFloppy } from '@mdi/js';
import Icon from '@mdi/react';
import { Input } from 'cd-eft/components/common/Input/Input';
import { SelectOption } from 'cd-eft/components/common/SelectOption/SelectOption';
// import { Notes } from '../Notes/Notes';
import { RainfallDischargeData } from '../RainfallDischargeData';
import { DelineateWatershedInputStateVM } from '../DelineateWatershedInput/DelineateWatershedInputStateVM';
import { UndoButton } from '../GISBox/UndoButton';
import { DownloadButton } from 'cd-eft/components/common/DownloadButton';
import { DUHDetails, SRDDetails, AdjuCurveDetails } from '../GISBox/WADetails';
import { ProjectComments } from 'cd-eft/components/Projects/ProjectComments/ProjectComments';

const AppStore = stores.AppStore;

const BasicDataFormComponent = (props: IBasicDataFormComponentProps) => {
  const history = useHistory();
  const vm = useRef(new BasicDataFormVM(history, props.activityViewModel)).current;
  useEffect(() => {
    vm.initPage();
  }, []);

  useEffect(() => {
    vm.disableSubmit = false;
    vm.disableDownloads = true;
  }, [ vm.allProjectandDesignComments]);

  const date = new Date();
  const dateStr =
    ('00' + (date.getMonth() + 1)).slice(-2) +
    ('00' + date.getDate()).slice(-2) +
    date.getFullYear() +
    ('00' + date.getHours()).slice(-2) +
    ('00' + date.getMinutes()).slice(-2);

  return (
    <div>
      <Modal
        className="eft-action-modal"
        isOpen={vm.projectSaveModalOpen}
        toggle={vm.saveProjModalToggle}
        centered={true}
      >
        <ModalHeader toggle={vm.saveProjModalToggle}>
          <Icon path={mdiFloppy} title="Edit" size={1} color="#005ea2" />
          <strong>Would you like to save as a project?</strong>
        </ModalHeader>
        <ModalBody>
          <Input
            label="Give your project a name."
            idAndName="projectName"
            type="text"
            value={vm.newProjectName}
            onChange={vm.handleInputChange}
            required={true}
            errorMsg={vm.projectNameValidation.projectNameErrors}
            valid={vm.projectNameValidation.projectNameValid}
            className={vm.errorClass('projectName')}
          />
        </ModalBody>
        <ModalFooter>
          <button className="usa-button usa-button--outline" onClick={vm.saveProjModalToggle}>
            Close
          </button>
          <button
            className="usa-button"
            onClick={() => {
              //validate the form before saving...
              vm.validateField('projectName', vm.newProjectName);
              if (vm.projectNameValidation.projectNameValid) {
                vm.saveNewProject(vm.newProjectName);
              }
            }}
          >
            Save &amp; Continue
          </button>
        </ModalFooter>
      </Modal>

      <LoadingSpinner message="" loading={vm.isLoading} outerClassName="basic-data-loader" />
      <div className="basic-data-form">
        <h2>Watershed Characteristics</h2>

        <div className="grid-row grid-gap">
          <div className="mobile-lg:grid-col-4 bdf-column mb-3">
            <h3>
              <b>Weighted Curve Number </b>
              {AppStore.efh2Store.basicDataForm.runoffCN}
            </h3>
          </div>

          <div className="mobile-lg:grid-col-4 bdf-column mb-3">
            <h3>
              <b>Watershed Length </b>
              {AppStore.efh2Store.basicDataForm.watershedLen} feet
            </h3>
          </div>
          <div className="mobile-lg:grid-col-4 bdf-column">
            <h3>
              <b>Watershed Slope </b>
              {AppStore.efh2Store.basicDataForm.watershedSlope} %
            </h3>
          </div>
          <div className="mobile-lg:grid-col-4 bdf-column mb-3">
            <div className='input-acn'>
              <Input
                label="Adjusted Curve Number"
                idAndName="adjustedCurveNumber"
                aria-label="adjusted curve number"
                valid={vm.formValidation.adjustedCurveNumberValid}
                value={vm.adjustedCurveNumber}
                type="number"
                onChange={(e) => {
                  vm.handleInputChange(e);
                }}
                step={1}
                min={1}
                onBlur={(e) => vm.validateField(e.target.name, e.target.value)}
                className={vm.errorClass('adjustedCurveNumber')}
                disabled={vm.disableAdjustedCurve}
                helpIcon={true}
                helpIconCallback={() => props.openHelpModal('Adjusted Curve Number', <AdjuCurveDetails />)}
              />
            </div>
          </div>
          <div className="mobile-lg:grid-col-4 bdf-column mb-3">
            <h3>
              <b>Time of Concentration </b>
              {AppStore.efh2Store.basicDataForm.timeC} hours (Lag method)
            </h3>
          </div>

          <div className="mobile-lg:grid-col-4 bdf-column mb-3">
            <h3>
              <b>Drainage Area </b>
              {AppStore.efh2Store.basicDataForm.drainageArea} acres
            </h3>
          </div>
          <div className="mobile-lg:grid-col-4 bdf-column mb-3">
            <SelectOption
              label="Dimensionless Unit Hydrograph"
              className="basic-data-form-select"
              idAndName="dimUnitHydrogID"
              options={vm.dimUnitHydrogOptions.map((x) => {
                return { value: x.id, name: x.name };
              })}
              value={AppStore.efh2Store.basicDataForm.dimUnitHydrogID}
              onChange={(event) => {
                vm.handleInputChange(event);
                AppStore.userProfileStore.setPreference(
                  'analysis-hydrology-project',
                  'dimUnitHydrogID',
                  AppStore.efh2Store.basicDataForm.dimUnitHydrogID
                );
              }}
              required={true}
              onBasicDataForm={true}
              removeDefaultOption={true}
              helpIcon={true}
              helpIconCallback={() => props.openHelpModal('Dimensionless Unit Hydrograph', <DUHDetails /* title='Dimensionless Unit Hydrograph' */ />)}
            />
          </div>
          <div className="mobile-lg:grid-col-4 bdf-column mb-3">
            <h3>
              <b>Rainfall Distribution Type </b>
              {AppStore.efh2Store.basicDataForm.state !== "OR" ? 'NOAA Atlas 14 Site Specific' : 'NOAA Atlas 2'}
            </h3>
          </div>
          <div className="mobile-lg:grid-col-4 bdf-column mb-3"></div>
          <div className="mobile-lg:grid-col-4 bdf-column mb-3">
            <SelectOption
              label="Smooth Rainfall Distribution"
              className="basic-data-form-select"
              idAndName="rdt_smoothing"
              options={[
                { value: true, name: 'Yes' },
                { value: false, name: 'No' },
              ]}
              value={AppStore.efh2Store.basicDataForm.rdt_smoothing}
              onChange={vm.handleInputChange}
              onBasicDataForm={true}
              required={true}
              removeDefaultOption={true}
              helpIcon={true}
              disabled={AppStore.efh2Store.basicDataForm.state === "OR"}
              helpIconCallback={() => props.openHelpModal('Smooth Rainfall Distribution', <SRDDetails /* title='Smooth Rainfall Distribution' */ />)}
            />
          </div>
        </div>

        <div className="grid-row grid-gap">
          <div className="mobile-lg:grid-col-12">
            {/* <Notes autosave={true} onRainfallAndDrainage={true} /> */}
            <ProjectComments projectId={AppStore.efh2Store.projectsStore.currentProject.projectUuid}  updateCallback={()=>vm.getAllProjectComments(AppStore.efh2Store.projectsStore.currentProject.projectUuid)} listAllComments={true} allCommentsData ={vm.allProjectandDesignComments}/>
            <RainfallDischargeData />
            <div className="button-container">
              <input
                type="submit"
                className="usa-button submit-button"
                value="Submit"
                disabled={vm.disableSubmit}
                onClick={vm.handleSubmit}
              />
              <span className="back-and-download-buttons">
                <UndoButton activityViewModel={props.activityViewModel} textButton={true} />
                <DownloadButton
                  href=''
                  download={`NETS_Hydrology_DetailReport_${dateStr}.pdf`}
                  disabled={vm.disableDownloads}
                  createLink={vm.downloadReport}
                >
                  Download Report
                </DownloadButton>
                <DownloadButton
                  href=''
                  download={`NETS_Hydrology_Details_${dateStr}.zip`}
                  disabled={vm.disableDownloads}
                  createLink={vm.downloadDetails}
                >
                  Download Detail Files
                </DownloadButton>
                <DownloadButton href="" download disabled={vm.disableDownloads} createLink={vm.createGISFilesLink}>
                  Download GIS Files
                </DownloadButton>
              </span>
            </div>
            <input
              type="button"
              className="usa-button usa-button--outline float-right margin-1"
              value="Save"
              hidden={stores.AppStore.efh2Store.projectsStore.currentProject.projectUuid?.length > 0}
              onClick={vm.saveProjModalToggle}
              disabled={vm.projectSaveModalOpen}
            />
          </div>
        </div>

        <div className="">
          <Alert show={vm.error} type="error" msg={vm.errorMsg} />
        </div>
      </div>
      <SlopeCalc callback={vm.callbackSlope} hidden={vm.hideSlopeCalc} close={vm.toggleSlopeCalc} />
    </div>
  );
};

export const BasicDataForm = observer(BasicDataFormComponent);

interface IBasicDataFormComponentProps {
  activityViewModel: DelineateWatershedInputStateVM;
  createGISFilesLink: any;
  openHelpModal: any;
}
